import React, { useState, useRef, useCallback } from 'react';
import './DraggablePromptChips.css';

/**
 * DraggablePromptChips
 * Renders prompt words as draggable chips. Users can reorder via drag-and-drop
 * and remove individual words by clicking ✕.
 *
 * Props:
 *   words    — string[]  — the current prompt split into tokens
 *   onChange — (words: string[]) => void — called whenever order/removal changes
 *   disabled — boolean
 */
export default function DraggablePromptChips({ words, onChange, disabled }) {
  const [dragIndex, setDragIndex] = useState(null);   // chip being dragged
  const [overIndex, setOverIndex] = useState(null);   // chip being hovered over
  const dragNode = useRef(null);

  const handleDragStart = useCallback((e, index) => {
    if (disabled) return;
    dragNode.current = e.currentTarget;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
    // Brief delay so the ghost doesn't show the "being dragged" style immediately
    setTimeout(() => {
      if (dragNode.current) dragNode.current.classList.add('dragging');
    }, 0);
  }, [disabled]);

  const handleDragEnd = useCallback(() => {
    if (dragNode.current) dragNode.current.classList.remove('dragging');
    dragNode.current = null;
    setDragIndex(null);
    setOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== overIndex) setOverIndex(index);
  }, [overIndex]);

  const handleDragLeave = useCallback(() => {
    setOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(fromIndex) || fromIndex === dropIndex) {
      setOverIndex(null);
      return;
    }
    const updated = [...words];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(dropIndex, 0, moved);
    onChange(updated);
    setOverIndex(null);
  }, [words, onChange]);

  const handleRemove = useCallback((index) => {
    if (disabled) return;
    const updated = words.filter((_, i) => i !== index);
    onChange(updated);
  }, [words, onChange, disabled]);

  if (!words || words.length === 0) return null;

  return (
    <div className="dpc-container" aria-label="Drag to reorder prompt words">
      <span className="dpc-label">✦ Drag to reorder</span>
      <div className="dpc-chips" role="list">
        {words.map((word, i) => (
          <span
            key={`${word}-${i}`}
            className={[
              'dpc-chip',
              dragIndex === i ? 'dpc-chip--dragging' : '',
              overIndex === i && dragIndex !== i ? 'dpc-chip--over' : '',
              disabled ? 'dpc-chip--disabled' : '',
            ].filter(Boolean).join(' ')}
            draggable={!disabled}
            role="listitem"
            aria-label={`Word: ${word}. Drag to reorder, click ✕ to remove.`}
            onDragStart={(e) => handleDragStart(e, i)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, i)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, i)}
            title="Drag to reorder"
          >
            <span className="dpc-chip-drag-handle" aria-hidden="true">⠿</span>
            <span className="dpc-chip-text">{word}</span>
            <button
              type="button"
              className="dpc-chip-remove"
              onClick={() => handleRemove(i)}
              disabled={disabled}
              aria-label={`Remove "${word}"`}
              tabIndex={0}
              title={`Remove "${word}"`}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
